/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Radio from 'backbone.radio';

import GridView from '@/views/GridView';
import ButtonCell from '@/backgrid/ButtonCell';
import PageableCollection from '@/backbone/PageableCollection';
import OrderStatuses from '@/enums/OrderStatuses';
import showModal from '@/util/showModal';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import generateCSV from '@/util/generateCSV';
import downloadCSV from '@/util/downloadCSV';
import DeliveryScheduleModalView from '@/views/DeliveryScheduleModalView';
import SaveDeliverySchedules from '@/services/SaveDeliverySchedules';

import './A3DeliveryScheduleGridView.css';

const A3DeliveryScheduleGridView = GridView.extend({
  className: 'a3-delivery-schedule-grid-view horizontal-scroll',

  childEvents: {
    'next:clicked': 'onNextClicked',
    'previous:clicked': 'onPreviousClicked'
  },

  initialize() {
    const self = this;
    let orderTypeId;

    const orderDetails = this.model.get('orderDetails');
    const isSubmitted = !orderDetails || orderDetails.orderDetails.statusId !== OrderStatuses.INTERNAL_DRAFT;
    const showInternalSubmittedFields = !EXTERNAL && isSubmitted;
    this.model.set('showInternalSubmittedFields', showInternalSubmittedFields);

    if (orderDetails) {
      orderTypeId = this.model.get('orderDetails').orderTypeId;
      this.getNotesForSchedules();
      this.getFinancialPOCForSchedules();
    } else if (this.model.get('orderPackage')) {
      orderTypeId = this.model.get('orderPackage').orderFormTypeID;
    } else {
      orderTypeId = this.model.get('awPackage').orderFormTypeId;
    }
    this.model.set('orderTypeId', orderTypeId);
    this.formatAddressCells(this.model.get('schedules'));

    this.model.get('columns').push({
      name: 'editViewButton',
      label: '',
      width: 7,
      headerCell: 'custom',
      cell: ButtonCell.extend({
        name() {
          if (self.options.editable) {
            return 'Edit';
          }
          return 'View';
        },
        onClick(e, model) {
          self.options.editable
            ? self.onClickEdit(e, model, self.options.objectTypeCode)
            : self.onClickView(e, model, self.options.objectTypeCode);
        },
        buttonClass: 'btn-sm btn-outline-primary text-center'
      }),
      editable: false,
      sortable: false
    });
    this.options.columns = this.model.get('columns');

    this.options.columns,
      (this.options.collection = new PageableCollection(this.model.get('schedules'), {
        pageSize: 50
      }));
    this.options.emptyText = 'No Devices Selected';
    this.options.width = '4500px';

    GridView.prototype.initialize?.apply(this, this.options);
  },

  getNotesForSchedules() {
    const defaultNote = Radio.channel('order').request('get:defaultNote');
    this.model.set('note', defaultNote);

    _.each(this.model.get('schedules'), (schedule) => {
      if (!schedule.note) {
        schedule.note = this.model.get('note');
      }
    });
  },

  getFinancialPOCForSchedules() {
    const finPoc = Radio.channel('order').request('get:finPoc');
    const hasFullName = finPoc.firstName && finPoc.lastName;

    this.model.set('finPOCName', hasFullName ? `${finPoc.firstName} ${finPoc.lastName}` : '');
    this.model.set('finPOCEmail', finPoc.email || '');
    this.model.set('finPOCPhone', finPoc.telephone || '');

    _.each(this.model.get('schedules'), (schedule) => {
      if (!schedule.finPOCName) {
        schedule.finPOCName = this.model.get('finPOCName');
      }
      if (!schedule.finPOCEmail) {
        schedule.finPOCEmail = this.model.get('finPOCEmail');
      }
      if (!schedule.finPOCPhone) {
        schedule.finPOCPhone = this.model.get('finPOCPhone');
      }
    });
  },

  formatAddressCells(data, isCSV) {
    _.each(data, (row) => {
      Object.assign(row, row.address);
      row.completeAddress1 = [
        row.addressLine1,
        row.addressLine2,
        row.buildingFloorNumber,
        row.floorNumber,
        row.roomPoleNumber
      ]
        .filter((x) => x)
        .join(' ');
      row.completeAddress2 = [row.city, row.regionCode, row.zip].filter((x) => x).join(' ');
      if (isCSV) {
        row.volumeBand = row.device.volumeBand;
        row.notes = this.model.get('note') ? this.model.get('note') : row.note;
      }
    });

    return data;
  },

  openPreviousItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    const objectTypeCode = this.options.objectTypeCode;
    if (this.options.editable) {
      this.onClickEdit(e, newModel, objectTypeCode);
    } else {
      this.onClickView(e, newModel, objectTypeCode);
    }
  },

  openNextItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    const objectTypeCode = this.options.objectTypeCode;
    if (this.options.editable) {
      this.onClickEdit(e, newModel, objectTypeCode);
    } else {
      this.onClickView(e, newModel, objectTypeCode);
    }
  },

  async saveRowItem(modalData, model) {
    // Manually find region, additional service, and training names from lookup data
    let dataAddress = modalData.formData.address;
    const region = _.find(this.model.get('regions'), (x) => {
      return x.id === dataAddress.regionId;
    });
    dataAddress.regionName = region?.name;
    dataAddress.regionCode = region?.code;

    const service = _.find(this.model.get('additionalServices'), (x) => {
      return x.id === modalData.formData.additionalServiceId;
    });
    modalData.formData.additionalServiceText = service?.name;
    modalData.formData.additionalServiceCLIN = service?.clin;

    const accessory = _.find(this.model.get('additionalAccessories'), (x) => {
      return x.id === modalData.formData.additionalAccessoryId;
    });
    modalData.formData.additionalAccessoryText = accessory?.name;

    const training = _.find(this.model.get('trainingTypes'), (x) => {
      return x.id === modalData.formData.trainingTypeId;
    });
    modalData.formData.trainingTypeText = training?.name;

    // Set row model data in the UI
    if (dataAddress.zip) {
      dataAddress.isConus = '1';
    }
    dataAddress = this.formatAddressCells([modalData.formData]);

    model.set('termingSerialNumber', modalData.formData.termingSerialNumber);
    model.set('isReplacementText', modalData.formData.isReplacement === '1' ? 'Replacement' : 'New');
    model.set('network', modalData.formData.network);
    model.set('trainingTypeText', modalData.formData.trainingTypeText);
    model.set('additionalServiceText', modalData.formData.additionalServiceText);
    model.set('additionalServiceCLIN', modalData.formData.additionalServiceCLIN);
    model.set('additionalAccessoryText', modalData.formData.additionalAccessoryText);

    model.set('commandEchelon', modalData.formData.commandEchelon);
    model.set('address', dataAddress[0]);
    model.set('completeAddress1', dataAddress[0].completeAddress1);
    model.set('completeAddress2', dataAddress[0].completeAddress2);

    model.set('deliveryPOCName', modalData.formData.deliveryPOCName);
    model.set('deliveryPOCEmail', modalData.formData.deliveryPOCEmail);
    model.set('deliveryPOCPhone', modalData.formData.deliveryPOCPhone);
    const deliveryNames = model.get('deliveryPOCName').split(' ');
    const deliveryPOCGivenNames = deliveryNames
      .slice(0, deliveryNames.length <= 2 ? 1 : deliveryNames.length - 1)
      .join(' ');
    const deliveryPOCLastName = deliveryNames
      .slice(deliveryNames.length > 1 ? deliveryNames.length - 1 : deliveryNames.length)
      .join(' ');
    model.set('deliveryPOCFirstName', deliveryPOCGivenNames);
    model.set('deliveryPOCLastName', deliveryPOCLastName);

    if (this.options.itPOCExists) {
      model.set('itPOCName', modalData.formData.itPOCName);
      model.set('itPOCEmail', modalData.formData.itPOCEmail);
      model.set('itPOCPhone', modalData.formData.itPOCPhone);
      const itNames = model.get('itPOCName').split(' ');
      const itPOCGivenNames = itNames.slice(0, itNames.length <= 2 ? 1 : itNames.length - 1).join(' ');
      const itPOCLastName = itNames.slice(itNames.length > 1 ? itNames.length - 1 : itNames.length).join(' ');
      model.set('itPOCFirstName', itPOCGivenNames);
      model.set('itPOCLastName', itPOCLastName);
    }

    if (this.options.finPOCExists) {
      model.set('finPOCName', modalData.formData.finPOCName);
      model.set('finPOCEmail', modalData.formData.finPOCEmail);
      model.set('finPOCPhone', modalData.formData.finPOCPhone);
      const itNames = model.get('finPOCName').split(' ');
      const finPOCGivenNames = itNames.slice(0, itNames.length <= 2 ? 1 : itNames.length - 1).join(' ');
      const finPOCLastName = itNames.slice(itNames.length > 1 ? itNames.length - 1 : itNames.length).join(' ');
      model.set('finPOCFirstName', finPOCGivenNames);
      model.set('finPOCLastName', finPOCLastName);
    }

    const index = _.findIndex(this.model.get('schedules'), ['id', model.get('id')]);
    this.model.get('schedules')[index] = model.toJSON();
    const deliverySchedules = [model.toJSON()];

    try {
      await SaveDeliverySchedules(this.options.objectID, this.options.objectTypeCode, deliverySchedules);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Delivery Schedule Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveDeliveryScheduleError',
        errorTitle: 'Save Error',
        errorMessage:
          'An error occurred while saving this line item in the delivery schedule. Please try again or contact an administrator.'
      });
    }
  },

  async onClickEdit(e, model, objectTypeCode) {
    if (e?.type) {
      e.preventDefault();
    }

    model.set('regions', this.model.get('regions'));
    model.set('commandPOCs', this.model.get('commandPOCs'));
    model.set('trainingTypes', this.model.get('trainingTypes'));
    model.set('isReplacementOptions', this.model.get('isReplacementOptions'));
    model.set('additionalServices', this.model.get('additionalServices'));
    model.set('additionalAccessories', this.model.get('additionalAccessories'));
    model.set('isOtherCountry', this.model.get('isOtherCountry'));
    model.set('isMFD', this.model.get('isMFD'));
    model.set('showInternalSubmittedFields', this.model.get('showInternalSubmittedFields'));
    model.set('orderTypeID', this.model.get('orderTypeId'));
    model.set('conusOconusRegions', this.model.get('conusOconusRegions'));
    await showModal({
      title: 'Delivery Schedule Details',
      view: new DeliveryScheduleModalView({
        objectTypeCode,
        isReadOnly: false,
        itPOCExists: !!this.options.itPOCExists,
        finPOCExists: !!this.options.finPOCExists,
        model
      }),
      dialogClasses: 'modal-lg'
    }).then((modalData) => {
      _.each(Object.keys(modalData.formData), (key) => {
        model.set(key, modalData.formData[key]);
      });

      if (modalData.button === 'prev') {
        this.saveRowItem(modalData, model);
        this.openPreviousItem(modalData);
      } else if (modalData.button === 'next') {
        this.saveRowItem(modalData, model);
        this.openNextItem(modalData);
      } else if (modalData.button === 'save') {
        this.saveRowItem(modalData, model);
      }
    });
  },

  async onClickView(e, model, objectTypeCode) {
    if (e?.type) {
      e.preventDefault();
    }

    model.set('regions', this.model.get('regions'));
    model.set('trainingTypes', this.model.get('trainingTypes'));
    model.set('isReplacementOptions', this.model.get('isReplacementOptions'));
    model.set('additionalServices', this.model.get('additionalServices'));
    model.set('additionalAccessories', this.model.get('additionalAccessories'));
    model.set('isMFD', this.model.get('isMFD'));
    model.set('showInternalSubmittedFields', this.model.get('showInternalSubmittedFields'));
    model.set('orderTypeID', this.model.get('orderTypeId'));
    model.set('conusOconusRegions', this.model.get('conusOconusRegions'));
    const modalData = await showModal({
      title: 'Delivery Schedule Details',
      view: new DeliveryScheduleModalView({
        objectTypeCode,
        isReadOnly: true,
        itPOCExists: !!this.options.itPOCExists,
        finPOCExists: !!this.options.finPOCExists,
        model
      }),
      dialogClasses: 'modal-lg'
    });
    if (modalData.button === 'prev') {
      this.openPreviousItem(modalData);
    } else if (modalData.button === 'next') {
      this.openNextItem(modalData);
    }
  },

  async getCSVColumns() {
    const columnsToInclude = _.reduce(
      this.options.columns.toJSON(),
      (acc, column) => {
        if (column.downloadable && column.label) {
          acc.push({
            name: column.name,
            label: column.label
          });
        }
        return acc;
      },
      []
    );
    return columnsToInclude;
  },

  async downloadTemplateCSV() {
    const columnsToInclude = await this.getCSVColumns();
    const csvContent = await generateCSV([], columnsToInclude);
    const fileName = 'PRTNR A3 Delivery Schedule CSV Template';
    await downloadCSV(csvContent, fileName);
  },

  async generateAndDownloadCSV() {
    try {
      const columnsToInclude = await this.getCSVColumns();

      const data = this.options.collection.fullCollection.toJSON();
      this.formatAddressCells(data, true);

      const csvContent = await generateCSV(data, columnsToInclude);

      //build file name
      const isAwPackage = this.options.objectTypeCode === 'AW_PACKAGE';
      const isPrtnrA3 = this.model.get('awPackage')?.orderFormTypeId === '13';
      let fileName;

      if (isAwPackage && isPrtnrA3) {
        const trackerNumber = data[0].trackerNumber;
        const awPackageId = this.options.objectID;
        const orderType = this.model.get('awPackage').formName;
        const commandName = data[0].commandName;

        const includeTrackerNumber = trackerNumber ? trackerNumber : 'Unknown';

        fileName = `Tracker # ${includeTrackerNumber}, AW Package # ${awPackageId}, Order Type ${orderType}, ${commandName}`;
      } else {
        fileName = `${this.options.objectTypeCode.toLowerCase()}_${this.options.objectID}_delivery_schedule`;
      }

      await downloadCSV(csvContent, fileName);
    } catch (err) {
      showErrorMessage({
        errorCode: 'downloadError',
        errorTitle: 'Download Error',
        errorMessage: 'And error occurred while downloading delivery schedule CSV.'
      });
    }
  },

  removePunctuation(val) {
    if (!val) return;
    return val.replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, '');
  }
});

export default A3DeliveryScheduleGridView;
